import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import MsftGraphMessageQuery from '../../graphql/queries/system/MsftGraphMessageQuery.graphql';
import MsftGraphMessageSaveMutation from '../../graphql/mutations/system/MsftGraphMessageSaveMutation.graphql';
import { Spinner } from '@fluentui/react/lib/Spinner';
import styled from 'styled-components';
import {
  IGraphMessage,
  IMsftGraphMessageQueryVariables,
  IMsftGraphSaveMessageInput,
  IMsftGraphSaveMessagePayload,
} from '../../graphql/schema/p4types';
import { format } from 'date-fns';
import { Image } from '@fluentui/react/lib/Image';
import { mdiChevronDoubleLeft } from '@mdi/js';
import Icon from '@mdi/react';
import getClientMutationId from '../../utils/getClientMutationId';
import { PrimaryButton } from '../../components/connectedForm/Button';
import CheckPopUpBlockerFunction from '../../utils/CheckPopUpBlockerFunction';
import { useDispatch } from 'react-redux';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';

const Container = styled.div`
  height: 94%;
  width: 100%;
`;

const InnerContainer = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Metadata = styled.div`
  margin: 1em;
  max-width: 284px;
`;

const Header = styled.div`
  flex-shrink: 0;
`;

const Main = styled.div`
  flex-grow: 1;
`;

const Footer = styled.div`
  flex-shrink: 0;
`;

const Title = styled.div`
  display: flex;
  margin: 1em 0.5em;

  h1 {
    margin: 0;
    color: #1582d8;
  }
`;

const TitleItem = styled.div`
  margin-left: 0.5em;
`;

const Iframe = styled.iframe`
  height: 100%;
  border: none;
  margin: 0;
  padding: 0;
`;

const Subject = styled.p`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const EmailAddress = ({ name, address }: { name: string; address: string }) => {
  return (
    <>
      {name} {'<' + address + '>'}
    </>
  );
};

export default function MailItemSidebar({
  itemId,
  user,
  onClose,
}: {
  itemId: string;
  user: string;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const handleQueryError = useComponentQueryErrorHandling();

  const { loading, data } = useQuery<
    {
      msftGraphMessage: IGraphMessage;
    },
    IMsftGraphMessageQueryVariables
  >(MsftGraphMessageQuery, {
    variables: {
      user,
      id: itemId,
    },
    onError: handleQueryError,
  });
  const [saveMessage] = useMutation<
    { msftGraphSaveMessage: IMsftGraphSaveMessagePayload },
    { input: IMsftGraphSaveMessageInput }
  >(MsftGraphMessageSaveMutation, {
    onError: handleQueryError,
  });

  const onDownload = useCallback(() => {
    saveMessage({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          user: data?.msftGraphMessage.mailbox,
          id: itemId,
          temporary: true,
        },
      },
    }).then((response) =>
      CheckPopUpBlockerFunction(
        { dispatch },
        `/api/resources/temporary/${response.data?.msftGraphSaveMessage.result}`,
        '_blank'
      )
    );
  }, [data?.msftGraphMessage, itemId]);

  if (loading) {
    return (
      <Metadata>
        <Spinner />
      </Metadata>
    );
  }

  const msftGraphMessage = data?.msftGraphMessage;

  return (
    <Container>
      <InnerContainer>
        <Header>
          <Title>
            <TitleItem onClick={onClose} style={{ cursor: 'pointer' }}>
              <Icon path={mdiChevronDoubleLeft} size={1} />
            </TitleItem>
            <TitleItem style={{ marginLeft: '0.5em' }}>
              <Image src="/images/outlook.svg" alt="Outlook" width={30} />
            </TitleItem>
            <TitleItem>
              <h1>Outlook</h1>
            </TitleItem>
          </Title>
          <Metadata>
            {msftGraphMessage ? (
              <>
                <Subject title={msftGraphMessage.subject!}>
                  <strong>Subject: </strong> {msftGraphMessage.subject}
                </Subject>
                <p>
                  <strong>Received:</strong>{' '}
                  {format(new Date(msftGraphMessage.receivedDateTime), 'dd/MM/yyyy HH:ii:ss')}
                </p>
                <p>
                  <strong>From:</strong>{' '}
                  <EmailAddress name={msftGraphMessage.from!.name!} address={msftGraphMessage.from!.address!} />
                </p>
                <p>
                  <strong>To: </strong>
                  {msftGraphMessage.to!.map((to, index) => {
                    return (
                      <>
                        <EmailAddress key={to!.name!} name={to!.name!} address={to!.address!} />
                        {index !== msftGraphMessage.to!.length - 1 ? ', ' : ''}
                      </>
                    );
                  })}
                </p>
              </>
            ) : (
              'No message found'
            )}
          </Metadata>
        </Header>
        {msftGraphMessage ? (
          <>
            <Main>
              {msftGraphMessage.contentType === 'HTML' ? (
                <Iframe srcDoc={msftGraphMessage.body!} referrerPolicy="no-referrer" sandbox="allow-popups" />
              ) : (
                <p>{msftGraphMessage.body}</p>
              )}
            </Main>
            <Footer>
              <PrimaryButton onClick={onDownload}>Download File</PrimaryButton>
            </Footer>
          </>
        ) : null}
      </InnerContainer>
    </Container>
  );
}
